import { useEffect, useId, useRef, useState } from 'react'
import type { DragEvent, KeyboardEvent, ReactNode } from 'react'
import { Icon } from '@iconify/react'

/* Small accessible form primitives: visible labels, hints and errors wired
   through aria-describedby, aria-invalid on the control. */

export const Ic = ({ name, className = '' }: { name: string; className?: string }) => (
  <Icon icon={`lucide:${name}`} className={`bk-ic ${className}`} aria-hidden='true' />
)

export function ErrorText({ id, msg }: { id: string; msg?: string }) {
  if (!msg) return null
  return (
    <p className='bk-err' id={`${id}-err`}>
      <Ic name='circle-alert' />
      <span>{msg}</span>
    </p>
  )
}

const describedBy = (id: string, hint?: ReactNode, error?: string) =>
  [hint ? `${id}-hint` : '', error ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined

type FieldProps = {
  id: string
  label: ReactNode
  value: string | number
  onChange: (v: string) => void
  hint?: ReactNode
  optional?: boolean
  error?: string
  pre?: ReactNode
  suf?: ReactNode
  type?: string
  inputMode?: 'text' | 'decimal' | 'numeric' | 'tel' | 'email'
  maxLength?: number
  min?: number | string
  step?: string
  readOnly?: boolean
  placeholder?: string
  onBlur?: () => void
}

export function Field(p: FieldProps) {
  return (
    <div className='bk-field'>
      <label htmlFor={p.id}>
        {p.label}
        {p.optional && <span className='bk-optional'> (optional)</span>}
      </label>
      {p.hint && (
        <p className='bk-hint' id={`${p.id}-hint`}>
          {p.hint}
        </p>
      )}
      <div className={`bk-control ${p.pre ? 'has-pre' : ''} ${p.suf ? 'has-suf' : ''}`}>
        {p.pre && <span className='bk-affix pre'>{p.pre}</span>}
        <input
          className='bk-inp'
          id={p.id}
          type={p.type || 'text'}
          inputMode={p.inputMode}
          value={p.value}
          maxLength={p.maxLength}
          min={p.min}
          step={p.step}
          readOnly={p.readOnly}
          placeholder={p.placeholder}
          autoComplete='off'
          aria-invalid={p.error ? true : undefined}
          aria-describedby={describedBy(p.id, p.hint, p.error)}
          onChange={(e) => p.onChange(e.target.value)}
          onBlur={p.onBlur}
        />
        {p.suf && <span className='bk-affix suf'>{p.suf}</span>}
      </div>
      <ErrorText id={p.id} msg={p.error} />
    </div>
  )
}

type Option = { value: string; label: string }

export function SelectField(p: {
  id: string
  label: ReactNode
  value: string
  onChange: (v: string) => void
  options: Option[]
  placeholder?: string
  hint?: ReactNode
  error?: string
  disabled?: boolean
}) {
  return (
    <div className='bk-field'>
      <label htmlFor={p.id}>{p.label}</label>
      {p.hint && (
        <p className='bk-hint' id={`${p.id}-hint`}>
          {p.hint}
        </p>
      )}
      <select
        className='bk-inp'
        id={p.id}
        value={p.value}
        disabled={p.disabled}
        aria-invalid={p.error ? true : undefined}
        aria-describedby={describedBy(p.id, p.hint, p.error)}
        onChange={(e) => p.onChange(e.target.value)}
      >
        <option value=''>{p.placeholder || 'Select'}</option>
        {p.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ErrorText id={p.id} msg={p.error} />
    </div>
  )
}

export type Card = {
  value: string
  title: ReactNode
  sub?: ReactNode
  icon?: string
  disabled?: boolean
}

export function OptionCards(p: {
  name: string
  legend: string
  hideLegend?: boolean
  value: string
  onChange: (v: string) => void
  options: Card[]
  error?: string
  cols?: 1 | 2
}) {
  return (
    <fieldset className='bk-grp tight'>
      {/* A legend can't be visually hidden itself (it's laid out in the fieldset border), so hide its text */}
      {p.hideLegend ? (
        <legend className='bk-legend-sr'>
          <span className='bk-sr'>{p.legend}</span>
        </legend>
      ) : (
        <legend>{p.legend}</legend>
      )}
      <div className={`bk-opts ${p.cols === 1 ? '' : 'c2'}`} aria-invalid={p.error ? true : undefined}>
        {p.options.map((o, n) => (
          <label className='bk-opt' key={o.value}>
            <input
              type='radio'
              name={p.name}
              id={n === 0 ? p.name : `${p.name}-${n}`}
              value={o.value}
              checked={p.value === o.value}
              disabled={o.disabled}
              aria-describedby={n === 0 && p.error ? `${p.name}-err` : undefined}
              onChange={() => p.onChange(o.value)}
            />
            {o.icon && (
              <span className='oi'>
                <Ic name={o.icon} />
              </span>
            )}
            <span className='ot'>
              <b>{o.title}</b>
              {o.sub && <span>{o.sub}</span>}
            </span>
            <span className='ock'>
              <Ic name='check' />
            </span>
          </label>
        ))}
      </div>
      <ErrorText id={p.name} msg={p.error} />
    </fieldset>
  )
}

export function Chips(p: {
  name: string
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: ReactNode }[]
  error?: string
}) {
  const lid = useId()
  return (
    <div className='bk-field'>
      <span className='bk-lbl' id={lid}>
        {p.label}
      </span>
      <div className='bk-chips' role='radiogroup' aria-labelledby={lid} aria-invalid={p.error ? true : undefined}>
        {p.options.map((o, n) => (
          <label className='bk-chip' key={o.value}>
            <input
              type='radio'
              name={p.name}
              id={n === 0 ? p.name : `${p.name}-${n}`}
              value={o.value}
              checked={p.value === o.value}
              aria-describedby={n === 0 && p.error ? `${p.name}-err` : undefined}
              onChange={() => p.onChange(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
      <ErrorText id={p.name} msg={p.error} />
    </div>
  )
}

export function NumStepper(p: {
  id: string
  value: string | number
  onChange: (v: string) => void
  label: string
  min?: number
  error?: string
  labelledBy?: string
}) {
  const min = p.min ?? 0
  const bump = (d: number) => p.onChange(String(Math.max(min, (parseInt(String(p.value), 10) || 0) + d)))
  return (
    <div className='bk-num'>
      <button type='button' onClick={() => bump(-1)} aria-label={`Decrease ${p.label}`}>
        <Ic name='minus' />
      </button>
      <input
        className='bk-inp'
        id={p.id}
        type='number'
        inputMode='numeric'
        min={min}
        value={p.value}
        aria-labelledby={p.labelledBy}
        aria-invalid={p.error ? true : undefined}
        aria-describedby={p.error ? `${p.id}-err` : undefined}
        onChange={(e) => p.onChange(e.target.value)}
      />
      <button type='button' onClick={() => bump(1)} aria-label={`Increase ${p.label}`}>
        <Ic name='plus' />
      </button>
    </div>
  )
}

export function Note({
  tone,
  icon,
  children,
  role,
}: {
  tone: 'info' | 'ok' | 'warn' | 'bad'
  icon: string
  children: ReactNode
  role?: string
}) {
  return (
    <div className={`bk-note ${tone}`} role={role}>
      <Ic name={icon} />
      <div>{children}</div>
    </div>
  )
}

/* ---------- Combobox (ARIA 1.2 pattern: input + listbox) ---------- */

export type ComboOption<T> = { key: string; title: string; sub?: string; value: T }

export function Combo<T>(p: {
  id: string
  label: string
  hint?: ReactNode
  value: string
  onInput: (v: string) => void
  options: ComboOption<T>[]
  onPick: (o: ComboOption<T>) => void
  loading?: boolean
  emptyText?: ReactNode
  listHead?: string
  error?: string
  placeholder?: string
  onBlur?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [idx, setIdx] = useState(-1)
  const wrap = useRef<HTMLDivElement>(null)
  const showList = open && p.options.length > 0
  const showEmpty = open && !p.loading && p.options.length === 0 && !!p.emptyText

  useEffect(() => setIdx(-1), [p.options])
  useEffect(() => {
    const out = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', out)
    return () => document.removeEventListener('mousedown', out)
  }, [])

  const pick = (o: ComboOption<T>) => {
    setOpen(false)
    p.onPick(o)
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setIdx((i) => Math.min(p.options.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIdx((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter' && showList && idx >= 0) {
      e.preventDefault()
      pick(p.options[idx])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className='bk-field bk-combo' ref={wrap}>
      <label htmlFor={p.id}>{p.label}</label>
      {p.hint && (
        <p className='bk-hint' id={`${p.id}-hint`}>
          {p.hint}
        </p>
      )}
      <div className='bk-control'>
        <Ic name={p.loading ? 'loader-circle' : 'search'} className={`lead ${p.loading ? 'spin' : ''}`} />
        <input
          className='bk-inp'
          id={p.id}
          type='text'
          role='combobox'
          autoComplete='off'
          aria-autocomplete='list'
          aria-expanded={showList}
          aria-controls={`${p.id}-lb`}
          aria-activedescendant={showList && idx >= 0 ? `${p.id}-o${idx}` : undefined}
          aria-invalid={p.error ? true : undefined}
          aria-describedby={describedBy(p.id, p.hint, p.error)}
          placeholder={p.placeholder}
          value={p.value}
          onChange={(e) => {
            setOpen(true)
            p.onInput(e.target.value)
          }}
          onFocus={() => setOpen(true)}
          onBlur={p.onBlur}
          onKeyDown={onKey}
        />
        <ul className='bk-lb' id={`${p.id}-lb`} role='listbox' aria-label={`${p.label} suggestions`} hidden={!showList}>
          {p.listHead && (
            <li className='bk-lb-head' role='presentation'>
              {p.listHead}
            </li>
          )}
          {p.options.map((o, n) => (
            <li
              key={o.key}
              id={`${p.id}-o${n}`}
              role='option'
              aria-selected={idx === n}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(o)
              }}
            >
              <b>{o.title}</b>
              {o.sub && <span>{o.sub}</span>}
            </li>
          ))}
        </ul>
        {showEmpty && <div className='bk-lb-empty'>{p.emptyText}</div>}
      </div>
      <div className='bk-sr' aria-live='polite'>
        {showList ? `${p.options.length} suggestion${p.options.length > 1 ? 's' : ''}. Use up and down arrows to choose.` : ''}
      </div>
      <ErrorText id={p.id} msg={p.error} />
    </div>
  )
}

/* ---------- Sheet: centred dialog on desktop, bottom sheet on phones ---------- */

export function Sheet(p: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const opener = useRef<Element | null>(null)
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (p.open && !d.open) {
      opener.current = document.activeElement
      d.showModal()
      const first = d.querySelector<HTMLElement>('.bk-sheet-body input:checked, .bk-sheet-body input, .bk-sheet-body button')
      first?.focus()
    } else if (!p.open && d.open) {
      d.close()
    }
  }, [p.open])
  return (
    <dialog
      ref={ref}
      className='bk-sheet'
      aria-labelledby='bk-sheet-h'
      onClose={() => {
        p.onClose()
        ;(opener.current as HTMLElement | null)?.focus?.()
      }}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog element itself
        if (e.target === ref.current) ref.current?.close()
      }}
    >
      <div className='bk-sheet-in'>
        <div className='bk-sheet-head'>
          <h2 id='bk-sheet-h'>{p.title}</h2>
          <button type='button' className='bk-sheet-x' aria-label='Close' onClick={() => ref.current?.close()}>
            <Ic name='x' />
          </button>
        </div>
        <div className='bk-sheet-body'>{p.open && p.children}</div>
        <div className='bk-sheet-foot'>
          <button type='button' className='bk-btn primary' onClick={() => ref.current?.close()}>
            Done
          </button>
        </div>
      </div>
    </dialog>
  )
}

/* ---------- Searchable select (combobox over a fixed list, e.g. countries) ---------- */

export function SearchSelect(p: {
  id: string
  label: ReactNode
  value: string
  options: Option[]
  onChange: (v: string) => void
  placeholder?: string
  hint?: ReactNode
  error?: string
}) {
  // null = not searching: the input shows the chosen option
  const [q, setQ] = useState<string | null>(null)
  const [idx, setIdx] = useState(0)
  const list = useRef<HTMLUListElement>(null)
  const chosen = p.options.find((o) => o.value === p.value)
  const open = q !== null
  const term = (q || '').trim().toLowerCase()
  const matches = !term
    ? p.options
    : [
        // Names that start with what was typed come first, then any other match
        ...p.options.filter((o) => o.label.toLowerCase().startsWith(term) || o.value.toLowerCase() === term),
        ...p.options.filter((o) => !o.label.toLowerCase().startsWith(term) && o.value.toLowerCase() !== term && o.label.toLowerCase().includes(term)),
      ]

  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`#${p.id}-o${idx}`)?.scrollIntoView({ block: 'nearest' })
  }, [idx, open])

  const pick = (o?: Option) => {
    if (o) p.onChange(o.value)
    setQ(null)
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setQ('')
      setIdx((i) => Math.min(matches.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIdx((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter' && open) {
      e.preventDefault()
      pick(matches[idx])
    } else if (e.key === 'Escape') setQ(null)
  }

  return (
    <div className='bk-field bk-combo bk-ssel'>
      <label htmlFor={p.id}>{p.label}</label>
      {p.hint && (
        <p className='bk-hint' id={`${p.id}-hint`}>
          {p.hint}
        </p>
      )}
      <div className='bk-control'>
        <Ic name='search' className='lead' />
        <input
          className='bk-inp'
          id={p.id}
          type='text'
          role='combobox'
          autoComplete='off'
          aria-autocomplete='list'
          aria-expanded={open}
          aria-controls={`${p.id}-lb`}
          aria-activedescendant={open && matches[idx] ? `${p.id}-o${idx}` : undefined}
          aria-invalid={p.error ? true : undefined}
          aria-describedby={describedBy(p.id, p.hint, p.error)}
          placeholder={chosen ? chosen.label : p.placeholder || 'Type to search'}
          value={open ? q : chosen?.label || ''}
          onFocus={() => {
            setQ('')
            setIdx(Math.max(0, p.options.findIndex((o) => o.value === p.value)))
          }}
          // Still focused after a pick: a click or a keystroke starts a fresh search
          onClick={() => !open && setQ('')}
          onChange={(e) => {
            setQ(open ? e.target.value : (e.nativeEvent as InputEvent).data || '')
            setIdx(0)
          }}
          onBlur={() => {
            // A single match counts as the choice, so typing "germ" and tabbing away works
            if (term && matches.length === 1) pick(matches[0])
            else setQ(null)
          }}
          onKeyDown={onKey}
        />
        <Ic name='chevron-down' className='trail' />
        <ul className='bk-lb' id={`${p.id}-lb`} role='listbox' aria-label={`${typeof p.label === 'string' ? p.label : 'Options'}`} hidden={!open} ref={list}>
          {matches.map((o, n) => (
            <li
              key={o.value}
              id={`${p.id}-o${n}`}
              role='option'
              aria-selected={idx === n}
              className={o.value === p.value ? 'chosen' : ''}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(o)
              }}
            >
              <b>{o.label}</b>
            </li>
          ))}
          {!matches.length && (
            <li className='bk-lb-head' role='presentation'>
              No country matches “{q}”
            </li>
          )}
        </ul>
      </div>
      <ErrorText id={p.id} msg={p.error} />
    </div>
  )
}

/* ---------- Drop zone: click to choose, or drag a file onto it ---------- */

export function DropZone({
  htmlFor,
  onFile,
  as = 'label',
  className = 'bk-drop',
  children,
}: {
  htmlFor: string
  onFile: (file?: File) => void
  as?: 'label' | 'div'
  className?: string
  children: ReactNode
}) {
  const [over, setOver] = useState(false)
  const depth = useRef(0) // dragenter/leave also fire for children
  const handlers = {
    onDragEnter: (e: DragEvent) => {
      e.preventDefault()
      depth.current++
      setOver(true)
    },
    onDragOver: (e: DragEvent) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1)
      if (!depth.current) setOver(false)
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault()
      depth.current = 0
      setOver(false)
      onFile(e.dataTransfer.files?.[0])
    },
  }
  const cls = `${className} ${over ? 'is-over' : ''}`
  return as === 'label' ? (
    <label htmlFor={htmlFor} className={cls} {...handlers}>
      {children}
    </label>
  ) : (
    <div className={cls} {...handlers}>
      {children}
    </div>
  )
}
